import { GameID, GamePredictionRecordsByGameID, IGamePrediction, SeasonID, SeasonWeekID } from '@shared/types';
import { Cls, Emojis } from 'app/constants';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { breakSeasonWeekID } from 'helpers/IdHelpers';
import { db } from '../lib/firebase';

const CONSOLE_ON = false; // Or import getConsoleState

/**
 * Fetches predictions for a specific season and model version from Firebase.
 *
 * @param {SeasonID} year - The season year (e.g., "2023")
 * @param {string} modelVersion - The model version (e.g., "1.0.1")
 * @returns {Promise<IGamePrediction[]>} - Array of prediction objects
 */
export const fetchGamePredictionsBySeasonID = async (
  year: SeasonID,
  modelVersion: string = ''
): Promise<GamePredictionRecordsByGameID> => {
  // Construct collection path: stats/{year}/teamresults/{year}-00-Season/{year}-Predictions{version}
  const collectionName = `stats/${year}/teamresults/${year}-00-Season/${year}-Predictions${modelVersion}`;

  if (CONSOLE_ON) console.log(`${Emojis.eyes} Fetching predictions from %c ${collectionName} %c`, Cls.Loc, '');

  try {
    const collectionRef = collection(db, collectionName);
    const snapshot = await getDocs(collectionRef);

    const predictions: GamePredictionRecordsByGameID = {};
    snapshot.forEach((doc) => {
      const data = doc.data() as IGamePrediction;
      predictions[doc.id as GameID] = data;
    });

    if (CONSOLE_ON)
      console.log(`${Emojis.eyes} Fetched %c ${Object.keys(predictions).length} %c predictions`, Cls.ValLg, '');
    return predictions;
  } catch (error) {
    console.error(`${Emojis.ex} Error fetching predictions:`, error);
    // A failed read must reach the worker error response, not erase cached predictions.
    throw error;
  }
};

/**
 * Alias for fetchGamePredictionsBySeasonID to maintain compatibility with existing code
 * @deprecated Use fetchGamePredictionsBySeasonID instead
 */
export const fetchPredictions = async (year: SeasonID, modelVersion: string): Promise<IGamePrediction[]> => {
  const predictionsMap = await fetchGamePredictionsBySeasonID(year, modelVersion);
  return Object.values(predictionsMap);
};

export const fetchGamePredictionsBySeasonWeekID = async (
  seasonWeekID: SeasonWeekID,
  modelVersion: string = ''
): Promise<GamePredictionRecordsByGameID> => {
  const { yearID } = breakSeasonWeekID(seasonWeekID);
  const path = `stats/${yearID}/teamresults/${yearID}-00-Season/${yearID}-Predictions${modelVersion}`;
  const q = query(collection(db, path), where('seasonWeekID', '==', seasonWeekID));
  const snapshot = await getDocs(q);

  const predictions: GamePredictionRecordsByGameID = {};

  snapshot.forEach((doc) => {
    const docID = doc.id;
    const prediction = doc.data() as IGamePrediction;
    predictions[docID as GameID] = prediction;
  });

  const count = Object.keys(predictions).length;
  const msg = `${Emojis.rocket} %c FETCHED %c %c ${count} %c GAME PREDICTION docs for %c ${seasonWeekID} %c`;
  if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Cnt, '', Cls.Val, '');
  return predictions;
};

/**
 * Fetch predictions filtered by model version
 * Useful for comparing accuracy across different model versions
 */
export const fetchGamePredictionsByVersion = async (
  seasonID: SeasonID,
  modelVersion: string
): Promise<GamePredictionRecordsByGameID> => {
  const allPredictions = await fetchGamePredictionsBySeasonID(seasonID, modelVersion);

  // Filter predictions by model version
  const filteredPredictions: GamePredictionRecordsByGameID = {};
  Object.entries(allPredictions).forEach(([gameID, prediction]) => {
    if (prediction.modelVersion === modelVersion) {
      filteredPredictions[gameID as GameID] = prediction;
    }
  });

  const count = Object.keys(filteredPredictions).length;
  const msg = `${Emojis.eyes} %c FILTERED PREDICTIONS %c version %c ${modelVersion} %c → %c ${count} %c games`;
  if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Val, '', Cls.Val, '');
  return filteredPredictions;
};
